import { Ban, Stamp } from "lucide-react";
import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Document } from "~/api/model";
import { Button } from "~/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useProjectParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import type { DocumentWithBates } from "~/types/bates";
import { trackEvent } from "~/utils/analytics";
import {
  isBatesEligible,
  useApplyBates,
  useUpdateBatesStatus,
} from "../../hooks/useBatesActions";

interface BatesStatusCellProps {
  document: DocumentWithBates;
}

/**
 * Renders the per-file Bates status in the document list.
 *
 * Possible states:
 *  – (dash)           batesStampingStatus is NONE or absent — shows inline Apply/Skip buttons
 *  Stamping           STAMPING (re-stamp in progress, numbers not on images yet)
 *  ABC-000001         STAMPED  (shows the first page's number)
 *  Pre-numbered       DETECTED_EXISTING — shows inline Apply/Skip buttons
 *  Skipped            SKIPPED
 */
export const BatesStatusCell: FC<BatesStatusCellProps> = ({
  document: doc,
}) => {
  const { formatMessage } = useIntl();
  const { caseId } = useProjectParams();
  const { toast } = useToast();
  const { mutateAsync: applyBates, isPending: isApplying } = useApplyBates();
  const { mutateAsync: updateBatesStatus, isPending: isSkipping } =
    useUpdateBatesStatus();
  const status = doc.batesStampingStatus;

  const isEligible = isBatesEligible(doc as Document);

  const handleApply = async () => {
    trackEvent({
      eventName: "app/case/bates_cell/apply.button/click",
      properties: { caseId, documentId: doc.id },
    });
    try {
      await applyBates([doc.id]);
      toast({
        title: formatMessage({
          id: "bates.applySuccess.title",
          defaultMessage: "Bates Numbers Applied",
        }),
        description: formatMessage({
          id: "bates.applySuccess.description",
          defaultMessage: "Stamping in progress — numbers will appear shortly.",
        }),
      });
    } catch {
      toast({
        variant: "destructive",
        title: formatMessage({
          id: "bates.actionError.title",
          defaultMessage: "Bates Action Failed",
        }),
        description: formatMessage({
          id: "bates.actionError.description",
          defaultMessage: "Could not update Bates status. Please try again.",
        }),
      });
    }
  };

  const handleSkip = async () => {
    trackEvent({
      eventName: "app/case/bates_cell/skip.button/click",
      properties: { caseId, documentId: doc.id },
    });
    try {
      await updateBatesStatus({ documentIds: [doc.id], status: "SKIPPED" });
    } catch {
      toast({
        variant: "destructive",
        title: formatMessage({
          id: "bates.actionError.title",
          defaultMessage: "Bates Action Failed",
        }),
        description: formatMessage({
          id: "bates.actionError.description",
          defaultMessage: "Could not update Bates status. Please try again.",
        }),
      });
    }
  };

  // Eligible files: show inline Apply / Skip buttons
  if (isEligible) {
    const isBusy = isApplying || isSkipping;
    const isPreNumbered = status === "DETECTED_EXISTING";
    return (
      <div className="flex flex-col overflow-hidden">
        {isPreNumbered && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-[10px] text-amber-600 dark:text-amber-400 leading-tight truncate cursor-default">
                <FormattedMessage
                  id="bates.status.preNumbered"
                  defaultMessage="Pre-numbered"
                />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              {(doc as DocumentWithBates).detectedBatesPrefix ? (
                <FormattedMessage
                  id="bates.status.detectedPrefix"
                  defaultMessage="Detected prefix: {prefix}"
                  values={{
                    prefix: (doc as DocumentWithBates).detectedBatesPrefix,
                  }}
                />
              ) : (
                <FormattedMessage
                  id="bates.status.existingDetected"
                  defaultMessage="Existing Bates numbers detected"
                />
              )}
            </TooltipContent>
          </Tooltip>
        )}
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950"
                onClick={handleApply}
                disabled={isBusy}
                aria-label={formatMessage({
                  id: "bates.inline.apply",
                  defaultMessage: "Apply",
                })}
              >
                <Stamp className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <FormattedMessage
                id="bates.inline.apply.tooltip"
                defaultMessage="Apply Bates numbering to this file"
              />
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                onClick={handleSkip}
                disabled={isBusy}
                aria-label={formatMessage({
                  id: "bates.inline.skip",
                  defaultMessage: "Skip",
                })}
              >
                <Ban className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <FormattedMessage
                id="bates.inline.skip.tooltip"
                defaultMessage="Skip Bates numbering for this file"
              />
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    );
  }

  if (status === "STAMPING") {
    return (
      <span className="text-xs text-muted-foreground truncate">
        <FormattedMessage
          id="documentsList.bates.stamping"
          defaultMessage="Stamping"
        />
      </span>
    );
  }

  if (status === "STAMPED") {
    return (
      <span className="text-xs font-mono text-foreground truncate">
        {doc.firstBatesNumber || (
          <FormattedMessage
            id="documentsList.bates.stamped"
            defaultMessage="Stamped"
          />
        )}
      </span>
    );
  }

  if (status === "SKIPPED") {
    return (
      <span className="text-xs text-muted-foreground">
        <FormattedMessage
          id="documentsList.bates.skipped"
          defaultMessage="Skipped"
        />
      </span>
    );
  }

  return <span className="text-muted-foreground">-</span>;
};
